<template>
<div class="calculated-task-modal">
  <transition name="modal">
    <div v-if="!materialModal && !fittingsModal && !fixingsModal" class="modal-mask task-modal-mask">
      <div class="modal-wrapper">
        <div class="modal-container">
          <app-sub-navigation-bar
            :segments="breadcrumbSegments"
            :view-mode="catalogViewMode"
            :is-at-root="isAtCategoryTreeRoot"
            :task-code="taskBreadcrumbCode"
            @navigate-all="onBreadcrumbNavigateAll"
            @navigate-segment="onBreadcrumbNavigateSegment"
          />

          <div class="task-modal-shell">
            <div class="task-modal-body">
              <div class="task-modal-layout">
                <div class="task-modal-main">
                  <h4 class="task-modal-title" v-if="!editTitle">{{ task.title }}</h4>
                  <div class="ui form" v-else>
                    <div class="field task-modal-title-field">
                      <textarea rows="4" v-model="task.title"></textarea>
                    </div>
                  </div>
                  <a v-if="!editTitle" class="task-modal-edit-link" @click="editTitle = true">Edit</a>
                  <a v-else class="task-modal-edit-link" @click="editTitle = false">Save</a>

                  <div class="task-modal-description" v-if="!editDescription">{{ task.description }}</div>
                  <div class="ui form" v-else>
                    <div class="field">
                      <textarea rows="4" v-model="task.description"></textarea>
                    </div>
                  </div>
                  <a v-if="!editDescription" class="task-modal-edit-link" @click="editDescription = true">Edit</a>
                  <a v-else class="task-modal-edit-link" @click="editDescription = false">Save</a>

                  <div class="task-modal-options">
                    <div class="task-modal-field">
                      <app-field-label>Quantity</app-field-label>
                      <app-quantity-stepper
                        :model-value="task.qty"
                        :min="1"
                        @update:model-value="task.qty = $event"
                      />
                    </div>
                  </div>

                  <hr class="task-modal-divider" />
                  <h5 class="task-modal-field-label">Enter calculations:</h5>

                  <div class="task-modal-calculations">
                    <article
                      v-for="(calculation, calcIndex) in task.calculations"
                      :key="'calc-' + calculation.id"
                      class="task-modal-calculation-card"
                    >
                      <header class="task-modal-calculation-card__header">
                        <h2 class="task-modal-calculation-card__title text-body-large-strong">Calculation {{ calcIndex + 1 }}</h2>
                        <app-accordion-toggle
                          :expanded="isCalculationExpanded(calculation.id)"
                          expanded-label="Collapse"
                          collapsed-label="Expand"
                          align="end"
                          @toggle="toggleCalculationExpanded(calculation.id)"
                        />
                      </header>

                      <div v-show="isCalculationExpanded(calculation.id)" class="task-modal-calculation-card__body">
                        <template v-if="isExcavation">
                          <div class="task-modal-calculation-row ui form">
                            <div class="field">
                              <app-field-label>Type</app-field-label>
                              <app-select
                                :defaultLabel="'Select'"
                                :items="cutFillTypeOptions"
                                :value="calculation.cut_fill_type"
                                @selected="onCutFillTypeSelected(calculation, $event)"
                              />
                            </div>
                            <div class="field">
                              <app-field-label>Qty</app-field-label>
                              <app-select
                                :defaultLabel="'Select'"
                                :items="$store.getters['task/getQtys']"
                                :value="String(calculation.qty != null ? calculation.qty : 1)"
                                @selected="onCalcQtySelected(calculation, $event)"
                              />
                            </div>
                          </div>

                          <app-excavation-dimension-fields :calculation="calculation" />

                          <div class="task-modal-calculation-block task-modal-calculation-block--difficulty">
                            <div class="field task-modal-calculation-difficulty">
                              <app-field-label>{{ calcLabels.difficulty }}</app-field-label>
                              <app-select
                                :defaultLabel="'Select'"
                                :items="soilOptionsForCalc(calculation)"
                                :value="calculation.soil_type || 'medium'"
                                @selected="onSoilTypeSelected(calculation, $event)"
                              />
                              <a
                                href="#"
                                class="task-modal-field-helper-link"
                                @click.prevent="showSoilTypeHelp = true"
                              >Learn about soil type</a>
                            </div>
                            <div
                              v-if="calcIndex > 0"
                              class="task-modal-calculation-side-col"
                            >
                              <button
                                type="button"
                                class="task-modal-calculation-remove"
                                aria-label="Remove calculation"
                                @click="deleteCalculation(calculation.id)"
                              >
                                <svg
                                  class="task-modal-calculation-remove-icon"
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  aria-hidden="true"
                                >
                                  <circle cx="12" cy="12" r="9.5" fill="#FFFFFF" stroke="#FF4C4C" stroke-width="1" />
                                  <path d="M9.05078 9.05078L14.9492 14.9492" stroke="#FF4C4C" stroke-width="1.5" stroke-linecap="round" />
                                  <path d="M14.9492 9.05078L9.05078 14.9492" stroke="#FF4C4C" stroke-width="1.5" stroke-linecap="round" />
                                </svg>
                                <span>Remove</span>
                              </button>
                            </div>
                          </div>
                        </template>

                        <template v-else>
                          <div class="task-modal-calculation-row ui form">
                            <div class="field">
                              <app-field-label>{{ calcLabels.primary }}</app-field-label>
                              <app-text-input
                                type="text"
                                readonly
                                class="task-modal-calculation-product-input"
                                :class="{ loading: loading === true }"
                                :placeholder="loading ? 'Loading…' : ('Select ' + calcLabels.primary.toLowerCase())"
                                :model-value="pipeLabel(calculation)"
                                @focus="showPipes(calculation)"
                              />
                            </div>
                            <div class="field">
                              <app-field-label>{{ calcLabels.quantity }}</app-field-label>
                              <app-text-input
                                type="number"
                                step="0.1"
                                placeholder="0.0"
                                :model-value="calculation.multiplier"
                                @update:model-value="calculation.multiplier = $event"
                              />
                            </div>
                          </div>

                          <template v-if="calculationHasProduct(calculation)">
                            <div v-if="showFixings" class="task-modal-calculation-block">
                              <div
                                v-if="activeFixings(calculation).length"
                                class="task-modal-calculation-section"
                              >
                                <div class="task-modal-calculation-section__header">
                                  <app-field-label>{{ calcLabels.fixings }}</app-field-label>
                                  <app-field-label class="task-modal-calculation-section__header-qty">Qty</app-field-label>
                                </div>
                                <div
                                  v-for="fixing in activeFixings(calculation)"
                                  :key="'fixing-' + calculation.id + '-' + fixing.id"
                                  class="task-modal-calculation-item-row"
                                >
                                  <div class="task-modal-calculation-item-name">{{ fixing.description }}</div>
                                  <app-quantity-stepper
                                    :model-value="fixing.qty"
                                    @update:model-value="setFixingQty(calculation, fixing, $event)"
                                  />
                                </div>
                              </div>

                              <a
                                href="#"
                                class="task-modal-calculation-add-link"
                                @click.prevent="openFixings(calculation)"
                              >
                                <i class="plus icon" aria-hidden="true"></i>
                                <span>Add {{ calcLabels.fixings }}</span>
                              </a>
                            </div>

                            <div v-if="showFittings" class="task-modal-calculation-block">
                              <div
                                v-if="activeFittings(calculation).length"
                                class="task-modal-calculation-section"
                              >
                                <div class="task-modal-calculation-section__header">
                                  <app-field-label>{{ calcLabels.fittings }}</app-field-label>
                                  <app-field-label class="task-modal-calculation-section__header-qty">Qty</app-field-label>
                                </div>
                                <div
                                  v-for="fitting in activeFittings(calculation)"
                                  :key="'fitting-' + calculation.id + '-' + fitting.id"
                                  class="task-modal-calculation-item-row"
                                >
                                  <div class="task-modal-calculation-item-name">{{ fitting.description }}</div>
                                  <app-quantity-stepper
                                    :model-value="fitting.qty"
                                    @update:model-value="setFittingQty(calculation, fitting, $event)"
                                  />
                                </div>
                              </div>

                              <a
                                href="#"
                                class="task-modal-calculation-add-link"
                                @click.prevent="openFittings(calculation)"
                              >
                                <i class="plus icon" aria-hidden="true"></i>
                                <span>Add {{ calcLabels.fittings }}</span>
                              </a>
                            </div>
                          </template>

                          <div
                            v-if="calculationHasProduct(calculation) || calcIndex > 0"
                            class="task-modal-calculation-block task-modal-calculation-block--difficulty"
                          >
                            <div class="field task-modal-calculation-difficulty">
                              <app-field-label>{{ calcLabels.difficulty }}</app-field-label>
                              <app-select
                                :defaultLabel="'Select'"
                                :items="$store.getters['task/getMultipliers']"
                                :value="calculation.location"
                                @selected="onDifficultySelected(calculation, $event)"
                              />
                            </div>
                            <div
                              v-if="calcIndex > 0"
                              class="task-modal-calculation-side-col"
                            >
                              <button
                                type="button"
                                class="task-modal-calculation-remove"
                                aria-label="Remove calculation"
                                @click="deleteCalculation(calculation.id)"
                              >
                                <svg
                                  class="task-modal-calculation-remove-icon"
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  aria-hidden="true"
                                >
                                  <circle cx="12" cy="12" r="9.5" fill="#FFFFFF" stroke="#FF4C4C" stroke-width="1" />
                                  <path d="M9.05078 9.05078L14.9492 14.9492" stroke="#FF4C4C" stroke-width="1.5" stroke-linecap="round" />
                                  <path d="M14.9492 9.05078L9.05078 14.9492" stroke="#FF4C4C" stroke-width="1.5" stroke-linecap="round" />
                                </svg>
                                <span>Remove</span>
                              </button>
                            </div>
                          </div>
                        </template>
                      </div>
                    </article>
                  </div>

                  <div class="task-modal-calc-actions">
                    <button type="button" class="ui secondary button" @click="addItem">
                      <i class="plus icon" aria-hidden="true"></i>
                      New Calculation
                    </button>
                    <div class="task-modal-show-calculations">
                      <app-checkbox
                        :model-value="!!task.show_variables"
                        label="Show calculations on quote"
                        @update:model-value="task.show_variables = $event"
                      />
                    </div>
                  </div>

                  <hr class="task-modal-divider" />

                  <app-task-modal-cost-summary
                    :total-price="price"
                    :product-cost="materialsBuyCost"
                    :product-margin-pct="displayProductMarginPct"
                    :gross-profit="displayGrossProfit"
                    :gst-amount="displayGstAmount"
                    :labour-time="labourTimeFormatted"
                    :labour-rate="displayLabourRate"
                    v-model:price-type="priceType"
                  >
                    <template #cta>
                      <a
                        href="#"
                        class="ui primary button task-modal-add-btn"
                        v-if="!task.item_id"
                        v-on:click.prevent="addTask(task)"
                      >Add to quote</a>
                      <a
                        href="#"
                        class="ui primary button task-modal-add-btn"
                        v-else
                        v-on:click.prevent="addTask(task)"
                      >Save</a>
                    </template>
                  </app-task-modal-cost-summary>

                  <div class="task-modal-compare">
                    <app-checkbox
                      :model-value="compareChecked"
                      :label="compareCheckboxLabel"
                      :disabled="compareMaxReached"
                      @update:model-value="onCompareCheckboxInput"
                    />
                  </div>
                </div>

                <div class="task-modal-image">
                  <div class="task-modal-image-frame">
                    <img v-if="task.hasOwnProperty('images') && task.images !== null" :src="getImageUrl(task)" :alt="task.title || ''">
                    <img v-if="!task.hasOwnProperty('images') || task.images === null" src="../../../../assets/img/no-image-available.jpg" alt="">
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>

  <app-select-modal v-if="fittingsModal"
    :title="'Select ' + calcLabels.fittings"
    :type="calcLabels.fittings"
    :options="options"
    :calculation="calculation"
    @payload="addFittings($event)"
    @close="fittingsModal=false">
  </app-select-modal>
  <app-select-modal v-if="fixingsModal"
    :title="'Select ' + calcLabels.fixings"
    :type="calcLabels.fixings"
    :options="options"
    :calculation="calculation"
    @payload="addFixings($event)"
    @close="fixingsModal=false">
  </app-select-modal>
  <app-select-material-modal v-if="materialModal"
    :parent_materials="pipes"
    :calculation="calculation"
    @payload="addMaterial($event)"
    @close="materialModal=false">
  </app-select-material-modal>
  <app-standard-modal
    :open="showSoilTypeHelp"
    title="Soil type"
    title-id="soil-type-help-title"
    size="small"
    @close="showSoilTypeHelp = false"
  >
    <p class="smardie-modal-help-intro">
      Soil Type multiplies excavation labour time. Choose a value that matches how hard the ground is to dig.
    </p>
    <div
      v-for="item in soilTypeHelpItems"
      :key="item.title"
      class="smardie-modal-help-item"
    >
      <h3>{{ item.title }} <span class="smardie-modal-help-rec">— {{ item.recommended }}</span></h3>
      <p>{{ item.description }}</p>
    </div>
    <template #footer>
      <button type="button" class="ui primary button" @click="showSoilTypeHelp = false">OK</button>
    </template>
  </app-standard-modal>
</div>
</template>
